import useRoutes from '@/hooks/useRoutes';
import { useThemeColors } from '@/hooks/useThemeColors';
import useInertiaData from '@/hooks/useInertiaData';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import { ClassData, CourseData, NextTopicData, TopicData } from '@/types';
import { cleanWordPressHtml } from '@/utils/cleanHtml';
import { Link, usePage } from '@inertiajs/react';
import {
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    Download,
    FileText,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ClassInfoProps {
    classData: ClassData;
    onCompletionChange?: (completed: boolean) => void;
    course?: CourseData;
    topic?: TopicData;
    isCompleted?: boolean;
    nextTopic?: NextTopicData | null;
    canAccessClass?: boolean;
}

const ClassInfo: React.FC<ClassInfoProps> = ({
    classData,
    onCompletionChange,
    course,
    topic,
    isCompleted: externalIsCompleted,
    nextTopic,
    canAccessClass = true,
}) => {
    const { t } = useTranslation();
    const { props } = usePage();
    const { route } = useRoutes();
    const { csrf_token } = useInertiaData();
    const [isCompleted, setIsCompleted] = useState(
        (props as any).is_class_completed || false
    );
    const colors = useThemeColors();
    const sortedTopicClasses = useMemo(
        () =>
            [...(topic?.classes ?? [])].sort(
                (a, b) =>
                    (a.order ?? Number.MAX_SAFE_INTEGER) -
                        (b.order ?? Number.MAX_SAFE_INTEGER) || a.id - b.id
            ),
        [topic?.classes]
    );

    // Sync local state with external prop
    useEffect(() => {
        if (externalIsCompleted !== undefined) {
            setIsCompleted(externalIsCompleted);
        }
    }, [externalIsCompleted]);

    // Get current class index and navigation for mobile
    const currentIndex = sortedTopicClasses.findIndex((c) => c.is_current);
    const previousClass =
        currentIndex > 0 ? sortedTopicClasses[currentIndex - 1] : null;
    const nextClass =
        currentIndex < sortedTopicClasses.length - 1
            ? sortedTopicClasses[currentIndex + 1]
            : null;
    const nextTopicHref =
        nextTopic?.type === 'assignment'
            ? `/assignments/${nextTopic.assignment_slug}/`
            : nextTopic?.class_id && course
              ? route('class-detail', {
                    slug: course.slug,
                    class_id: nextTopic.class_id,
                }).toString()
              : null;

    const isSubmittingRef = useRef(false);

    const handleNext = async (nextUrl: string) => {
        if (!isCompleted && csrf_token && course) {
            try {
                await fetch(
                    route('class-mark-complete', {
                        slug: course.slug,
                        class_id: classData.id,
                    }).toString(),
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: new URLSearchParams({
                            csrfmiddlewaretoken: csrf_token,
                            action: 'complete',
                        }),
                    }
                );
            } catch (_) {
                // navigate regardless
            }
        }
        window.location.href = nextUrl;
    };

    // Filter materials to only include PDFs
    const pdfMaterials =
        classData.materials?.filter((material) =>
            material.file_url?.toLowerCase().endsWith('.pdf')
        ) || [];

    // Filter materials that are NOT PDFs (downloadable files)
    const downloadableMaterials =
        classData.materials?.filter(
            (material) => !material.file_url?.toLowerCase().endsWith('.pdf')
        ) || [];

    const handleMarkComplete = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (isSubmittingRef.current || !csrf_token) {
            return;
        }

        const isUnmarking = isCompleted;
        isSubmittingRef.current = true;
        const clickedButton = event.currentTarget;
        clickedButton.disabled = true;
        clickedButton.classList.add('cursor-wait', 'opacity-80');

        const buttonLabel = clickedButton.querySelector('[data-mark-complete-label]');
        if (buttonLabel) {
            buttonLabel.textContent = isUnmarking
                ? t('course.unmarking')
                : t('course.marking');
        }

        // Create and submit a traditional form - browser handles redirect automatically
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = route('class-mark-complete', {
            slug: (props as any).course.slug,
            class_id: classData.id,
        }).toString();

        // Add CSRF token from Inertia shared data
        const csrfInput = document.createElement('input');
        csrfInput.type = 'hidden';
        csrfInput.name = 'csrfmiddlewaretoken';
        csrfInput.value = csrf_token;
        form.appendChild(csrfInput);

        const actionInput = document.createElement('input');
        actionInput.type = 'hidden';
        actionInput.name = 'action';
        actionInput.value = isUnmarking ? 'uncomplete' : 'complete';
        form.appendChild(actionInput);

        document.body.appendChild(form);
        form.submit();
    };

    return (
        <div className="border-t border-gray-700 bg-gray-800">
            <div className="mx-auto max-w-4xl px-6 py-6">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center text-sm text-gray-400">
                    {(props as any).section ? (
                        <>
                            <Link
                                href={route('section-detail', {
                                    slug: (props as any).course.slug,
                                    section_id: (props as any).section.id,
                                }).toString()}
                                className="transition-colors hover:text-gray-200"
                            >
                                {(props as any).section.title}
                            </Link>
                            <ChevronRight size={16} className="mx-2" />
                        </>
                    ) : null}
                    {(props as any).topic ? (
                        <Link
                            href={`/courses/${(props as any).course.slug}/topics/${(props as any).topic.id}`}
                            className="transition-colors hover:text-gray-200"
                        >
                            {(props as any).topic.title}
                        </Link>
                    ) : null}
                </div>

                {/* Class Title */}
                <h1 className="mb-2 text-2xl font-bold text-white">
                    {classData.title}
                </h1>

                {/* Class Description */}
                {classData.description && (
                    <div
                        className="html-content mb-4 leading-relaxed text-gray-300"
                        dangerouslySetInnerHTML={{ __html: cleanWordPressHtml(classData.description) }}
                    />
                )}

                {/* Materials Section - Only show PDFs when access is granted */}
                {(canAccessClass && pdfMaterials.length > 0) ? (
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-white">
                            {t('course.materials')}
                        </h3>
                        <div className="space-y-3">
                            {pdfMaterials.map((material) => (
                                <div
                                    key={material.id}
                                    className="rounded-lg border border-gray-600 bg-gray-700 p-4"
                                >
                                    <div className="mb-3 flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <FileText
                                                size={20}
                                                className="text-gray-300"
                                            />
                                            <h4 className="font-medium text-white">
                                                {material.title}
                                            </h4>
                                        </div>
                                        <a
                                            href={material.file_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center space-x-1 rounded bg-gray-600 px-3 py-1 text-sm font-medium text-white transition-colors hover:bg-gray-500"
                                            title={`${t('course.download')} ${material.title}`}
                                        >
                                            <Download size={16} />
                                            <span className="hidden sm:inline">
                                                {t('course.download')}
                                            </span>
                                        </a>
                                    </div>
                                    <div className="aspect-[4/3] w-full overflow-hidden rounded border border-gray-500">
                                        <iframe
                                            src={`${material.file_url}#toolbar=0&navpanes=0&scrollbar=0`}
                                            className="h-full w-full"
                                            title={material.title}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}

                {/* Downloadable Materials Section - Non-PDF files when access is granted */}
                {(canAccessClass && downloadableMaterials.length > 0) ? (
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-white">
                            {t('course.downloadableMaterials')}
                        </h3>
                        <div className="space-y-2">
                            {downloadableMaterials.map((material) => (
                                <a
                                    key={material.id}
                                    href={material.file_url}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-between rounded-lg border border-gray-600 bg-gray-700 p-4 transition-colors hover:bg-gray-600"
                                >
                                    <div className="flex items-center space-x-3">
                                        <FileText
                                            size={20}
                                            className="text-gray-300"
                                        />
                                        <span className="font-medium text-white">
                                            {material.title}
                                        </span>
                                    </div>
                                    <div className="flex items-center space-x-2 text-sm text-gray-300">
                                        <Download size={16} />
                                        <span>{t('course.download')}</span>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                ) : null}

                {/* Action Button - Only show when access is granted */}
                {canAccessClass ? (
                    <PrimaryButton
                        id="mark-class-complete-button"
                        type="button"
                        onClick={handleMarkComplete}
                        disabled={!csrf_token}
                        className={`flex items-center space-x-2 ${isCompleted ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    >
                        <CheckCircle size={20} />
                        <span data-mark-complete-label>
                            {isCompleted
                                ? t('course.unmarkAsComplete')
                                : t('course.markAsComplete')}
                        </span>
                    </PrimaryButton>
                ) : null}

                {/* Mobile Navigation - Only shown on smaller screens */}
                {(course && (previousClass || nextClass || nextTopic)) ? (
                    <div className="mt-6 flex items-center justify-between border-t border-gray-600 pt-6 lg:hidden">
                        {previousClass ? (
                            <a
                                href={route('class-detail', {
                                    slug: course.slug,
                                    class_id: previousClass.id,
                                }).toString()}
                                className="flex items-center space-x-2 rounded-lg border border-gray-600 bg-gray-700 px-4 py-2 text-gray-200 transition-colors hover:bg-gray-600"
                            >
                                <ChevronLeft size={16} />
                                <span className="text-sm font-medium">
                                    {t('course.previous')}
                                </span>
                            </a>
                        ) : (
                            <div></div>
                        )}

                        {nextClass ? (
                            <a
                                href={route('class-detail', {
                                    slug: course.slug,
                                    class_id: nextClass.id,
                                }).toString()}
                                onClick={(e) => {
                                    e.preventDefault();
                                    void handleNext(route('class-detail', { slug: course.slug, class_id: nextClass.id }).toString());
                                }}
                                className="btn-gradient flex items-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium"
                            >
                                <span>{t('course.next')}</span>
                                <ChevronRight size={16} />
                            </a>
                        ) : null}
                        {(!nextClass && nextTopic && nextTopicHref) ? (
                            <a
                                href={nextTopicHref}
                                onClick={(e) => {
                                    e.preventDefault();
                                    void handleNext(nextTopicHref);
                                }}
                                className="btn-gradient flex items-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium"
                            >
                                <span>
                                    {nextTopic.type === 'assignment'
                                        ? t('course.nextAssignment')
                                        : t('course.nextTopic')}
                                </span>
                                <ChevronRight size={16} />
                            </a>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default ClassInfo;
